import EmployeeSalaryDetailAPI from "issara-sdk/apis/EmployeeSalaryDetailAPI_users";
import EmployeeSalaryListCreateAPI from "issara-sdk/apis/EmployeeSalaryListCreateAPI_users";

import PayrollDetailSettingCreate from "issara-sdk/apis/PayrollDetailSettingCreate_apps_HRM";

import EmployeeSalarySerializer from "issara-sdk/types/EmployeeSalarySerializer_users";
import PayrollDetailSettingSerializer from "issara-sdk/types/PayrollDetailSettingSerializer_apps_HRM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapData,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { adToBeWithSetFormat, beToAd } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
  SELECT: "SELECT",
} as const;

export const SEQUENCE_NAME = "EmployeePayment" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeePaymentSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    detail: EmployeeSalaryType | null;
    filter: FilterType;
    items: EmployeeSalaryType[];
    payrollDetailSetting: PayrollDetailSettingSerializer | null;
  }> | null;
  masterOptions: MasterOptions;
}>;

export type EmployeeSalaryType = Partial<Omit<EmployeeSalarySerializer, "tax_type">> & {
  bank?: number | null;
  position_name?: string;
  tax_type?: string[];
  whtAmount?: string;
};

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "selectedEmployee">;

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type MasterData = MasterMapData<typeof Masters>;

type FilterType = Partial<{
  include_inactive: boolean;
}>;

export type RequiredFieldType = Partial<
  Record<
    | "amount"
    | "bank"
    | "bank_account"
    | "bank_branch"
    | "end_date"
    | "has_social_security"
    | "net_payment"
    | "start_date"
    | "tax_deduction_amount",
    string
  >
>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  masterData?: MasterData;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SELECT]: { id: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeePaymentSequence: null,
};

export const DataInitial: Data = {};

const Masters = [
  ["positionAll", {}],
  ["documentType", {}],
  ["revenueTaxType", {}],
  ["salaryPaymentType", {}],
  ["bankBranch", {}],
  ["bankAccountType", {}],
  ["languageScore", {}],
  ["levelSkillScore", {}],
] as const;

export const TAX_RATES = [
  {
    max: 150000,
    min: 0,
    rateNumeric: 0,
  },
  {
    max: 300000,
    min: 150001,
    rateNumeric: 0.05,
  },
  {
    max: 500000,
    min: 300001,
    rateNumeric: 0.1,
  },
  {
    max: 750000,
    min: 500001,
    rateNumeric: 0.15,
  },
  {
    max: 1000000,
    min: 750001,
    rateNumeric: 0.2,
  },
  {
    max: 2000000,
    min: 1000001,
    rateNumeric: 0.25,
  },
  {
    max: 5000000,
    min: 2000001,
    rateNumeric: 0.3,
  },
  {
    max: Infinity,
    min: 5000001,
    rateNumeric: 0.35,
  },
];

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      EmployeePaymentSequence: {
        ...state.EmployeePaymentSequence,
        sequenceIndex: "Action",
        filter: {},
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SELECT]: HandleSelect,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler = async (controller) => {
  const [result] = await PayrollDetailSettingCreate.list({
    apiToken: controller.apiToken,
    params: { limit: 1 },
  });

  const state = controller.getState();
  const detail = result?.items?.[0] || null;

  controller.setState({
    EmployeePaymentSequence: { ...state.EmployeePaymentSequence, payrollDetailSetting: detail },
  });
};

const HandleSearch: Handler = async (controller) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.EmployeePaymentSequence?.filter ?? {};

  await controller.handleEvent({ message: "GetMasterData", params: { masters: [Masters[0]] } });

  const [result] = await EmployeeSalaryListCreateAPI.list({
    apiToken: controller.apiToken,
    employee_id: state.selectedEmployee?.id,
    params: { include_inactive: filter.include_inactive },
  });

  state = controller.getState();

  const positions = state.masterOptions?.positionAll ?? [];

  const foundPosition = positions.find(
    (option) => Number(option.value) === state.selectedEmployee?.position
  );

  const affiliate: Record<string, any>[] = state.selectedEmployee?.affiliate || [];

  const items: EmployeeSalaryType[] = result?.items || [];
  const formattedItems = items.map((item) => {
    const affiliateName = Array.isArray(affiliate)
      ? affiliate.map((acc) => acc.name as string).join(", ")
      : "";

    return {
      ...item,
      affiliation_name: affiliateName,
      position_name: foundPosition?.text || "",
    };
  });

  controller.setState({
    EmployeePaymentSequence: { ...state.EmployeePaymentSequence, items: formattedItems },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: null },
  });
};

const HandleSelect: Handler<Params<Actions["SELECT"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const [result, error] = await EmployeeSalaryDetailAPI.retrieve({
    apiToken: controller.apiToken,
    employee_id: state.selectedEmployee?.id,
    pk: params.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  const bankBranch = controller.data.masterData?.bankBranch ?? [];
  const foundBankBranch = bankBranch.find((item) => item.id === result.bank_branch);

  controller.setState({
    EmployeePaymentSequence: {
      ...state.EmployeePaymentSequence,
      detail: {
        ...result,
        bank: foundBankBranch?.bank || null,
        bank_branch: foundBankBranch?.id || null,
        end_date: adToBeWithSetFormat(result.end_date),
        language_expired_at: adToBeWithSetFormat(result.language_expired_at || ""),
        start_date: adToBeWithSetFormat(result.start_date || ""),
      },
    },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const detail = state.EmployeePaymentSequence?.detail;

  const api = detail?.id ? EmployeeSalaryDetailAPI.update : EmployeeSalaryListCreateAPI.create;

  const [result, error] = await api({
    apiToken: controller.apiToken,
    data: {
      ...detail,
      end_date: detail?.end_date ? beToAd(detail.end_date)?.format("YYYY-MM-DD") : "",
      language_expired_at: detail?.language_expired_at
        ? beToAd(detail.language_expired_at || "")?.format("YYYY-MM-DD")
        : null,
      start_date: detail?.start_date ? beToAd(detail.start_date || "")?.format("YYYY-MM-DD") : "",
      tax_type: detail?.tax_type as any,
    },
    employee_id: state.selectedEmployee?.id,
    pk: detail?.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error, errorKey: BTN_ACTS.SAVE });

    return;
  }

  await HandleSelect(controller, { ...params, action: ACTIONS.SELECT, id: result.id });

  HandleSearch(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.SUCCESS },
  });
};
